import { FormEvent, ReactNode, useEffect, useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import {
  Band,
  bandStyle,
  Evidence,
  formatClock,
  PersonRef,
} from 'screening/mapV2';
import { BOOK_A_CALL_URL } from 'screening/demo';
import { BackDisc, MelonMark, PlayDisc } from './icons';
import {
  Body,
  Meta,
  Nested,
  Pill,
  PrimaryButton,
  Row,
  SectionTitle,
  Stack,
  VisuallyHidden,
} from './tokens';

const TINTS: Array<[string, string]> = [
  ['#DCE6F7', '#032170'],
  ['#F1E4DA', '#6A3B1C'],
  ['#DDEEE6', '#17623F'],
  ['#E9E2F5', '#4A2C7A'],
  ['#F7E7EC', '#7A2742'],
  ['#E2EEF0', '#1E5560'],
  ['#FBEFD9', '#7A4B00'],
];

export function tintFor(id: string) {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return TINTS[hash % TINTS.length];
}

const AvatarBox = styled.span<{ $size: number; $bg: string; $fg: string }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  font-family: ${({ $size }) =>
    $size >= 80 ? 'var(--tm-font-display)' : 'inherit'};
  font-weight: 700;
  font-size: ${({ $size }) => Math.round($size * 0.34)}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export function Avatar({
  person,
  size = 36,
}: {
  person: Pick<PersonRef, 'id' | 'initials' | 'photo_url'>;
  size?: 36 | 40 | 52 | 88;
}) {
  const [bg, fg] = tintFor(person.id);
  return (
    <AvatarBox $size={size} $bg={bg} $fg={fg} aria-hidden="true">
      {person.photo_url ? (
        <img src={person.photo_url} alt="" />
      ) : (
        person.initials
      )}
    </AvatarBox>
  );
}

const CHIP_SIZE = {
  sm: { font: 12, pad: '3px 9px' },
  md: { font: 13, pad: '4px 10px' },
  lg: { font: 14, pad: '6px 12px' },
};

export function BandChip({
  band,
  score,
  size = 'md',
  prefix,
  label,
}: {
  band: Band;
  score?: number;
  size?: keyof typeof CHIP_SIZE;
  prefix?: string;
  label?: string;
}) {
  const style = bandStyle(band);
  const chip = CHIP_SIZE[size];
  const word = label ?? style.label;
  return (
    <Pill
      $bg={style.bg}
      $fg={style.fg}
      $size={chip.font}
      style={{ padding: chip.pad }}
    >
      {prefix ? `${word} ${prefix}` : word}
      {typeof score === 'number' ? ` · ${score}` : ''}
    </Pill>
  );
}

export type Tone = 'advance' | 'hold' | 'reject' | 'neutral';

const TONE_COLOURS: Record<Tone, [string, string]> = {
  advance: ['var(--tm-strong-bg)', 'var(--tm-strong-fg)'],
  hold: ['var(--tm-stretch-bg)', 'var(--tm-stretch-fg)'],
  reject: ['var(--screening-reject-wash)', 'var(--screening-reject)'],
  neutral: ['var(--tm-chip)', 'var(--tm-text-2)'],
};

export function TonePill({
  tone,
  children,
}: {
  tone: Tone;
  children: ReactNode;
}) {
  const [bg, fg] = TONE_COLOURS[tone];
  return (
    <Pill $bg={bg} $fg={fg} $size={12}>
      {children}
    </Pill>
  );
}

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FilterPill = styled.button<{ $on: boolean }>`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid
    ${({ $on }) => ($on ? 'var(--tm-navy)' : 'var(--tm-border-input)')};
  background: ${({ $on }) => ($on ? 'var(--tm-navy)' : 'var(--tm-surface)')};
  color: ${({ $on }) => ($on ? '#ffffff' : 'var(--tm-text-2)')};
  font-family: inherit;
  font-size: 14px;
  font-weight: ${({ $on }) => ($on ? 600 : 400)};
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: ${({ $on }) => ($on ? 'var(--tm-navy)' : 'var(--tm-dash)')};
  }
`;

export function FilterPills<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <FilterRow role="group" aria-label={label}>
      {options.map((option) => (
        <FilterPill
          key={option.value}
          type="button"
          $on={option.value === value}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </FilterPill>
      ))}
    </FilterRow>
  );
}

const BackAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  align-self: flex-start;
  font-weight: 600;
  font-size: 15px;
`;

export function BackLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <Link href={href} passHref legacyBehavior>
      <BackAnchor>
        <BackDisc />
        {children}
      </BackAnchor>
    </Link>
  );
}

const ChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 0 12px 0 4px;
  border-radius: 999px;
  border: 1px solid var(--tm-border-input);
  background: var(--tm-surface);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--tm-navy);
  cursor: pointer;
  text-decoration: none;
  align-self: flex-start;

  &:hover {
    border-color: var(--tm-dash);
    text-decoration: none;
  }
`;

export function PlayChip({
  atSecs,
  label,
  onPlay,
  href,
  playing,
}: {
  atSecs: number;
  label: string;
  onPlay?: () => void;
  href?: string;
  playing?: boolean;
}) {
  const inner = (
    <>
      <PlayDisc playing={playing} />
      {formatClock(atSecs)}
    </>
  );
  if (!onPlay && href) {
    return (
      <Link href={href} passHref legacyBehavior>
        <ChipButton as="a" aria-label={label}>
          {inner}
        </ChipButton>
      </Link>
    );
  }
  return (
    <ChipButton type="button" aria-label={label} onClick={onPlay}>
      {inner}
    </ChipButton>
  );
}

const QuoteText = styled.p<{ $size: number }>`
  margin: 0;
  font-size: ${({ $size }) => $size}px;
  line-height: 1.5;
  color: var(--tm-text);
`;

export function quoteOf(evidence: { text: string; quoted: boolean }) {
  return evidence.quoted ? `\u201C${evidence.text}\u201D` : evidence.text;
}

export function EvidenceQuote({
  evidence,
  firstName,
  onPlay,
  playHref,
  heading,
  size = 16,
}: {
  evidence: Evidence;
  firstName: string;
  onPlay?: (secs: number) => void;
  playHref?: (secs: number) => string;
  heading?: string;
  size?: number;
}) {
  const at = typeof evidence.at_secs === 'number' ? evidence.at_secs : null;
  return (
    <Nested>
      {heading && (
        <Meta $size={13} style={{ fontWeight: 600 }}>
          {heading}
        </Meta>
      )}
      <QuoteText $size={size}>{quoteOf(evidence)}</QuoteText>
      {at !== null ? (
        <Row $gap={10}>
          <PlayChip
            atSecs={at}
            label={`Play this moment from ${firstName}'s conversation`}
            onPlay={onPlay ? () => onPlay(at) : undefined}
            href={playHref ? playHref(at) : undefined}
          />
          {!heading && <Meta $size={13}>{evidence.source}</Meta>}
        </Row>
      ) : (
        !heading && <Meta $size={13}>{evidence.source}</Meta>
      )}
    </Nested>
  );
}

const SearchForm = styled.form<{ $hero: boolean }>`
  width: 100%;
  margin-top: ${({ $hero }) => ($hero ? '14px' : '0')};
  background: var(--tm-surface);
  border: 1px solid var(--tm-border-input);
  border-radius: 20px;
  box-shadow: var(--tm-search-shadow);
  padding: 10px 10px 10px ${({ $hero }) => ($hero ? '20px' : '18px')};
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 640px) {
    padding-left: 14px;
    gap: 10px;
  }
`;

const SearchInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 17px;
  color: var(--tm-text);
  padding: 14px 0;

  &::placeholder {
    color: var(--tm-muted);
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const SearchButton = styled(PrimaryButton)`
  flex: 0 0 auto;
  min-height: 48px;
  padding: 0 20px;
  border-radius: 14px;
`;

export function MelonSearch({
  value = '',
  hero = false,
  onSubmit,
}: {
  value?: string;
  hero?: boolean;
  onSubmit: (query: string) => void;
}) {
  const [query, setQuery] = useState(value);
  useEffect(() => setQuery(value), [value]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = query.trim();
    if (next.length > 1) onSubmit(next);
  };
  return (
    <SearchForm role="search" $hero={hero} onSubmit={submit}>
      <MelonMark />
      <label htmlFor="melon-search">
        <VisuallyHidden>Ask Melon about your pool</VisuallyHidden>
      </label>
      <SearchInput
        id="melon-search"
        type="text"
        value={query}
        placeholder="Ask Melon, or search people and roles"
        onChange={(event) => setQuery(event.target.value)}
        autoComplete="off"
      />
      <SearchButton type="submit">Search</SearchButton>
    </SearchForm>
  );
}

export const SectionHead = ({
  id,
  title,
  lead,
  action,
}: {
  id: string;
  title: string;
  lead?: string;
  action?: ReactNode;
}) => (
  <HeadRow>
    <HeadText>
      <HeadTitle id={id}>{title}</HeadTitle>
      {lead && <Meta $size={15}>{lead}</Meta>}
    </HeadText>
    {action}
  </HeadRow>
);

const HeadRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
`;

const HeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const HeadTitle = styled.h2`
  margin: 0;
  font-family: var(--tm-font-display);
  font-size: 22px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--tm-ink);
`;

export const SampleNote = styled.p`
  margin: 0;
  padding: 10px 14px;
  border-radius: var(--tm-r-control);
  background: var(--tm-chip);
  color: var(--tm-text-2);
  font-size: 14px;
  line-height: 1.5;
`;

export function DemoPill() {
  return (
    <Pill
      $bg="var(--tm-stretch-bg)"
      $fg="var(--tm-stretch-fg)"
      $size={12}
      style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
    >
      Demo
    </Pill>
  );
}

const DemoBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  padding: 12px 16px;
  border: 1px solid var(--tm-border);
  border-radius: var(--tm-r-control);
  background: var(--tm-surface);
  font-size: 14px;
  line-height: 1.5;
  color: var(--tm-text-2);

  a {
    font-weight: 600;
    white-space: nowrap;
  }
`;

export function DemoNote({ children }: { children: ReactNode }) {
  return (
    <DemoBox role="note">
      <DemoPill />
      <span style={{ flex: '1 1 320px' }}>{children}</span>
      <a href={BOOK_A_CALL_URL} target="_blank" rel="noopener noreferrer">
        Book a call
      </a>
    </DemoBox>
  );
}

const CallCard = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px 32px;
  padding: 28px 32px;
  border: 1px solid var(--tm-border);
  border-radius: var(--tm-r-page);
  background: var(--tm-surface);

  @media (max-width: 640px) {
    padding: 20px;
    border-radius: var(--tm-r-card);
  }
`;

export function BookACallCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <CallCard aria-labelledby="book-call-h">
      <Stack $gap={8} style={{ flex: '1 1 420px' }}>
        <Row $gap={10}>
          <DemoPill />
          <SectionTitle id="book-call-h" $size={22}>
            {title}
          </SectionTitle>
        </Row>
        <Body $size={16}>{children}</Body>
      </Stack>
      <PrimaryButton
        as="a"
        href={BOOK_A_CALL_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Book a call
      </PrimaryButton>
    </CallCard>
  );
}
